import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  DocumentArrowUpIcon,
  CheckIcon,
  ArrowRightIcon,
  EnvelopeIcon,
  TruckIcon,
  UserIcon,
  ArrowLeftEndOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Header from './app/Header';
import Footer from './app/Footer';
import FooterCopyright from './app/FooterCopyright';
import supabase from './utils/supabaseClient';
import { isValidEmail } from './utils/validators';

const STATUS_META = {
  pendente:   { label: 'Aguardando análise', Icon: ClockIcon,            bg: 'bg-accent-50',  border: 'border-accent-200',  text: 'text-accent-600',  step: 1 },
  em_analise: { label: 'Em análise',          Icon: MagnifyingGlassIcon, bg: 'bg-brand-50',   border: 'border-brand-200',   text: 'text-brand-700',   step: 2 },
  aprovado:   { label: 'Aprovado',             Icon: CheckCircleIcon,     bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700',   step: 3 },
  rejeitado:  { label: 'Reprovado',            Icon: XCircleIcon,         bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-600',     step: 0 },
  inativo:    { label: 'Inativo',              Icon: ClockIcon,           bg: 'bg-ink-50',     border: 'border-ink-200',     text: 'text-ink-500',     step: 0 },
};

const TIMELINE_STEPS = [
  { key: 'pendente',   label: 'Inscrito',    Icon: UserIcon },
  { key: 'em_analise', label: 'Em análise',  Icon: MagnifyingGlassIcon },
  { key: 'aprovado',   label: 'Aprovado',    Icon: CheckCircleIcon },
];

const DOCS = [
  { field: 'cnh_url',    label: 'CNH' },
  { field: 'crlv_url',   label: 'CRLV' },
  { field: 'selfie_url', label: 'Selfie com CNH' },
];

const EntregadorPortal = () => {
  const [authState, setAuthState] = useState('loading');
  const [session, setSession] = useState(null);
  const [application, setApplication] = useState(undefined);
  const [email, setEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setAuthState(s ? 'authenticated' : 'unauthenticated');
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setAuthState(s ? 'authenticated' : 'unauthenticated');
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (authState !== 'authenticated' || !session) return;

    supabase
      .from('entregadores')
      .select('*')
      .limit(1)
      .single()
      .then(({ data, error }) => {
        if (error && error.code !== 'PGRST116') {
          toast.error('Erro ao carregar inscrição.');
          console.error(error);
        }
        setApplication(data ?? null);
      });
  }, [authState, session]);

  const handleSendLink = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error('Informe um e-mail válido.');
      return;
    }
    setSending(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin + '/entregador/status' },
    });
    setSending(false);

    if (error) {
      toast.error('Erro ao enviar link. Tente novamente.');
      console.error(error);
      return;
    }
    setLinkSent(true);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setApplication(undefined);
  };

  // ── Loading ──
  if (authState === 'loading') {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-40">
          <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
        </div>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  // ── Unauthenticated ──
  if (authState === 'unauthenticated') {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="relative overflow-hidden bg-white">
          <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
          <div className="relative container mx-auto px-4 lg:px-6 py-20 lg:py-28">
            <div className="max-w-md mx-auto">
              {linkSent ? (
                <div className="card p-8 text-center">
                  <div className="h-16 w-16 mx-auto rounded-3xl bg-brand-gradient text-white flex items-center justify-center shadow-glow">
                    <EnvelopeIcon className="h-8 w-8" />
                  </div>
                  <h1 className="mt-6 font-display text-2xl font-bold text-ink-900">
                    Verifique seu e-mail
                  </h1>
                  <p className="mt-3 text-ink-500">
                    Enviamos um link de acesso para{' '}
                    <strong className="text-ink-900">{email}</strong>.
                    Clique no link para acessar seu portal.
                  </p>
                  <button
                    onClick={() => setLinkSent(false)}
                    className="mt-6 text-sm text-brand-700 font-semibold hover:underline"
                  >
                    Usar outro e-mail
                  </button>
                </div>
              ) : (
                <div className="card p-8">
                  <div className="h-12 w-12 rounded-2xl bg-brand-gradient text-white flex items-center justify-center shadow-glow mb-5">
                    <TruckIcon className="h-6 w-6" />
                  </div>
                  <h1 className="font-display text-2xl font-bold text-ink-900">
                    Portal do entregador
                  </h1>
                  <p className="mt-2 text-ink-500">
                    Acesse com o e-mail que você usou no cadastro.
                    Vamos enviar um link de acesso seguro.
                  </p>

                  <form onSubmit={handleSendLink} className="mt-6 space-y-4" noValidate>
                    <div>
                      <label className="label" htmlFor="portal-email">E-mail</label>
                      <input
                        id="portal-email"
                        className="input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="voce@email.com"
                        autoComplete="email"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={sending}
                      className="btn-primary w-full gap-2"
                    >
                      {sending ? 'Enviando…' : (
                        <>
                          Receber link de acesso
                          <ArrowRightIcon className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </form>

                  <p className="mt-6 text-xs text-ink-400 text-center">
                    Ainda não se cadastrou?{' '}
                    <Link to="/quero-ser-entregador" className="text-brand-700 font-semibold hover:underline">
                      Faça sua inscrição
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  // ── Authenticated — loading application ──
  if (authState === 'authenticated' && application === undefined) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-40">
          <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
        </div>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  // ── Authenticated — no application ──
  if (authState === 'authenticated' && application === null) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="relative overflow-hidden bg-white">
          <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
          <div className="relative container mx-auto px-4 lg:px-6 py-20 lg:py-28">
            <div className="max-w-md mx-auto card p-8 text-center">
              <XCircleIcon className="h-12 w-12 text-ink-300 mx-auto" />
              <h2 className="mt-4 font-display text-xl font-bold text-ink-900">
                Nenhuma inscrição encontrada
              </h2>
              <p className="mt-2 text-ink-500 text-sm">
                Não encontramos inscrição para{' '}
                <strong className="text-ink-900">{session?.user?.email}</strong>.
              </p>
              <div className="mt-6 flex flex-col gap-3">
                <Link to="/quero-ser-entregador" className="btn-primary gap-2">
                  Fazer minha inscrição
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
                <button onClick={handleSignOut} className="btn-ghost text-sm gap-2">
                  <ArrowLeftEndOnRectangleIcon className="h-4 w-4" />
                  Sair
                </button>
              </div>
            </div>
          </div>
        </section>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  // ── Authenticated — application found ──
  const status = application?.status ?? 'pendente';
  const meta = STATUS_META[status] ?? STATUS_META.pendente;
  const StatusIcon = meta.Icon;
  const currentStep = meta.step;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
        <div className="relative container mx-auto px-4 lg:px-6 pt-12 pb-6 max-w-2xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="badge-brand mb-3">Portal do entregador</span>
              <h1 className="font-display text-display-lg text-ink-900">
                Olá, {application?.primeiro_nome || 'Entregador'}
              </h1>
              <p className="mt-2 text-ink-500">{session?.user?.email}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="btn-ghost text-sm gap-1.5 shrink-0 mt-1"
              title="Sair"
            >
              <ArrowLeftEndOnRectangleIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="container mx-auto px-4 lg:px-6 max-w-2xl space-y-6">

          {/* Status card */}
          <div className={`card p-6 border ${meta.border}`}>
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 ${meta.bg} ${meta.text}`}>
                <StatusIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm text-ink-500 font-medium">Status da inscrição</p>
                <p className={`text-xl font-display font-bold mt-0.5 ${meta.text}`}>
                  {meta.label}
                </p>
              </div>
            </div>

            {/* Timeline — só para o fluxo normal (não rejeitado/inativo) */}
            {status !== 'rejeitado' && status !== 'inativo' && (
              <ol className="flex items-center mt-6">
                {TIMELINE_STEPS.map(({ key, label, Icon: TIcon }, idx) => {
                  const done = currentStep > idx + 1;
                  const active = currentStep === idx + 1;
                  return (
                    <li key={key} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center gap-1">
                        <div
                          className={`h-8 w-8 rounded-xl flex items-center justify-center transition
                            ${done  ? 'bg-brand-gradient text-white shadow-glow' : ''}
                            ${active ? 'bg-brand-gradient text-white shadow-glow ring-4 ring-brand-100' : ''}
                            ${!done && !active ? 'bg-ink-100 text-ink-400' : ''}`}
                        >
                          {done ? <CheckIcon className="h-4 w-4" /> : <TIcon className="h-4 w-4" />}
                        </div>
                        <span className={`text-xs font-medium ${active ? 'text-ink-900' : 'text-ink-400'}`}>
                          {label}
                        </span>
                      </div>
                      {idx < TIMELINE_STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-2 mb-5 ${done ? 'bg-brand-500' : 'bg-ink-100'}`} />
                      )}
                    </li>
                  );
                })}
              </ol>
            )}

            {/* Mensagens contextuais */}
            {status === 'pendente' && (
              <p className="mt-4 text-sm text-ink-500 bg-accent-50/50 border border-accent-100 rounded-xl p-3">
                Sua inscrição foi recebida. Vamos analisar em breve e te confirmar pelo WhatsApp.
              </p>
            )}
            {status === 'em_analise' && (
              <p className="mt-4 text-sm text-ink-500 bg-brand-50 border border-brand-100 rounded-xl p-3">
                Nosso time está revisando seus dados e documentos. Em breve você receberá uma resposta.
              </p>
            )}
            {status === 'aprovado' && (
              <div className="mt-4 space-y-3">
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl p-3">
                  Parabéns! Sua conta está aprovada. Comece a receber pedidos agora.
                </p>
                <Link to="/entregador/app" className="btn-primary w-full gap-2">
                  Abrir app de entregador
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            )}
            {status === 'rejeitado' && (
              <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-3">
                Infelizmente sua inscrição não foi aprovada neste momento.
                Entre em contato pelo WhatsApp para mais informações.
              </p>
            )}
            {status === 'inativo' && (
              <p className="mt-4 text-sm text-ink-500 bg-ink-50 border border-ink-200 rounded-xl p-3">
                Sua conta está inativa. Entre em contato com o suporte.
              </p>
            )}
          </div>

          {/* Dados resumidos */}
          <div className="card p-6">
            <h2 className="font-display font-bold text-ink-900 mb-4">Dados da inscrição</h2>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ['Nome', `${application?.primeiro_nome ?? ''} ${application?.sobrenome ?? ''}`.trim()],
                ['CPF', application?.cpf],
                ['WhatsApp', application?.whatsapp],
                ['Cidade', [application?.bairro, application?.cidade].filter(Boolean).join(', ')],
                ['Veículo', application?.tipo_veiculo],
                ['CNH', application?.cnh_categoria ? `Cat. ${application.cnh_categoria}` : null],
                ['Placa', application?.veiculo_placa],
                ['Modelo', application?.veiculo_modelo],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-ink-100 pb-2 last:border-0">
                  <dt className="text-ink-500">{k}</dt>
                  <dd className="text-ink-900 font-medium text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Documentos */}
          <div className="card p-6">
            <h2 className="font-display font-bold text-ink-900 mb-4">Documentos enviados</h2>
            <ul className="space-y-3">
              {DOCS.map(({ field, label }) => {
                const uploaded = !!application?.[field];
                return (
                  <li key={field} className="flex items-center gap-3">
                    <div
                      className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0
                        ${uploaded ? 'bg-brand-gradient text-white' : 'bg-ink-100 text-ink-400'}`}
                    >
                      {uploaded
                        ? <CheckIcon className="h-4 w-4" />
                        : <DocumentArrowUpIcon className="h-4 w-4" />}
                    </div>
                    <span className={`text-sm font-medium ${uploaded ? 'text-ink-900' : 'text-ink-400'}`}>
                      {label}
                    </span>
                    <span className={`ml-auto text-xs font-semibold ${uploaded ? 'text-brand-700' : 'text-ink-400'}`}>
                      {uploaded ? 'Enviado' : 'Pendente'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* CTA WhatsApp (sempre visível) */}
          <div className="card p-5 flex items-center gap-4 bg-ink-50 border-ink-100">
            <div className="h-10 w-10 rounded-xl bg-ink-200 text-ink-600 flex items-center justify-center shrink-0">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-ink-900 text-sm">Precisa de ajuda?</p>
              <p className="text-xs text-ink-500">Fale com nossa equipe pelo WhatsApp.</p>
            </div>
            <a
              href="https://wa.me/556299999999"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm ml-auto"
            >
              Abrir WhatsApp
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <FooterCopyright />
    </div>
  );
};

export default EntregadorPortal;
